import { sentry } from '@frontend/tracking';
import { GetCalendarEventIO } from '../types';
import { useSchedulerV3Queries } from './use-scheduler-v3-queries';
import { UseSchedulerV3QueryEndpointArgs, useSchedulerV3Query } from './use-scheduler-v3-query';

/**
 * Validates GetCalendarEvent request parameters after query execution
 */
const validateCalendarEventRequest = (request: GetCalendarEventIO['input']) => {
  if (!request?.referenceId || !request?.startDate) {
    console.error('[useGetCalendarEvent] referenceId and startDate are required for partitioned queries.');
    sentry.error({
      topic: 'schedule-calendar',
      team: 'schedule',
      error: 'referenceId and startDate are required for partitioned queries.',
      addContext: { name: 'details', context: { request } },
    });
  }
};

/**
 * Creates onSettled handler that validates the request parameters
 */
const createValidationOnSettled = <E = unknown, D = GetCalendarEventIO['output']>(
  originalRequest: UseSchedulerV3QueryEndpointArgs<'GetCalendarEvent', E, D>['request'],
  originalOnSettled?: (data: D | undefined, error: E | null) => void
) => {
  return (data: D | undefined, error: E | null) => {
    // Resolve the request to validate it
    const resolvedRequest = typeof originalRequest === 'function' ? originalRequest({}) : originalRequest;
    validateCalendarEventRequest(resolvedRequest);

    // Call the original onSettled if it exists
    if (originalOnSettled) {
      originalOnSettled(data, error);
    }
  };
};

/**
 * A hook that returns a query for the `GetCalendarEvent` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetCalendarEventQuery = <E = unknown, D = GetCalendarEventIO['output']>(
  args: UseSchedulerV3QueryEndpointArgs<'GetCalendarEvent', E, D>
) =>
  useSchedulerV3Query({
    endpointName: 'GetCalendarEvent',
    ...args,
    options: {
      ...args.options,
      onSettled: createValidationOnSettled(args.request, args.options?.onSettled),
    },
  });

/**
 * A hook that returns an array of queries for the `GetCalendarEvent` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetCalendarEventQueries = <E = unknown, D = GetCalendarEventIO['output']>(
  argsArr: UseSchedulerV3QueryEndpointArgs<'GetCalendarEvent', E, D>[]
) =>
  useSchedulerV3Queries(
    argsArr.map((args) => ({
      endpointName: 'GetCalendarEvent',
      ...args,
      options: {
        ...args.options,
        onSettled: createValidationOnSettled(args.request, args.options?.onSettled),
      },
    }))
  );
